import { numberToUserDisplay } from '@ebiz/pretty-num';
import ModelService from 'photodiode/services/model';
import ENV from '../config/environment';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';

export interface cdpAnalyticsOptions {
  sId?: string;
  reqId?: string;
  name?: string;
  url?: string;
  timeStart?: Date;
  timeEnd?: Date;
  status?: { code: number; message: string };
}

type cdpPayload = {
  sId?: string;
  reqId?: string;
  name?: string;
  url?: string;
  time?: number;
  status?: number;
  statusText?: string;
  stamp?: string;
};

const getElapsedInformation = function (currTime: number, startTime: number) {
  const endDateTime = new Date(currTime),
    startDateTime = new Date(startTime),
    deltaLoadTime = +currTime - +startTime;

  return 'startDate: ' + startDateTime + ', endDate: ' + endDateTime + ', loadTime(ms): ' + deltaLoadTime;
};

const buildAnalyticsData = function (model: ModelService) {
  let payload =
    'Vr: {VrBias}; {VrPolarity}; PD cap: {PdCap}; Rshunt: {Rshunt}; Ipeak: {Ipeak}; Vpeak: {Vpeak}; Pulse Width: {PulseWidth}; Slider: {SliderSetting}; Stages: {NumStages}; Op Amps: {OpAmps}';
  payload = payload.replace(
    '{VrBias}',
    numberToUserDisplay(model.state.Vr, 'Engineering', 3, {
      showZeroes: false,
    }),
  );
  payload = payload.replace('{VrPolarity}', model.state.Bias === '+' ? 'positive' : 'negative');
  payload = payload.replace(
    '{PdCap}',
    numberToUserDisplay(model.state.Cd, 'Engineering', 3, {
      showZeroes: false,
    }),
  );
  payload = payload.replace(
    '{Rshunt}',
    numberToUserDisplay(model.state.Rsh, 'Engineering', 3, {
      showZeroes: false,
    }),
  );
  payload = payload.replace(
    '{Ipeak}',
    numberToUserDisplay(model.state.Ip, 'Engineering', 3, {
      showZeroes: false,
    }),
  );
  payload = payload.replace(
    '{Vpeak}',
    numberToUserDisplay(model.state.Vp, 'Engineering', 3, {
      showZeroes: false,
    }),
  );
  payload = payload.replace(
    '{PulseWidth}',
    numberToUserDisplay(
      model.state.SpeedPreference === 'Bandwidth' ? 2 / model.state.TargetSpeed : model.state.TargetSpeed,
      'Engineering',
      3,
      { showZeroes: false },
    ),
  );
  const sliderValue = (10 * Math.log(6 * model.state.Peaking)) / Math.log(8) + 0.1;
  payload = payload.replace('{SliderSetting}', parseFloat(sliderValue.toPrecision(2)).toString());
  payload = payload.replace('{NumStages}', model.state.StageCount.toString());
  const opAmpList = [];
  if (model.opamps) {
    opAmpList.push(model.opAmp1.partNumber + ' - ' + (model.state.OpAmp1 ? 'Locked' : 'Not Locked'));
    if (model.state.StageCount > 1 && model.opAmp2) {
      opAmpList.push(model.opAmp2.partNumber + ' - ' + (model.state.OpAmp2 ? 'Locked' : 'Not Locked'));
    }
  }
  payload = payload.replace('{OpAmps}', opAmpList.length > 0 ? opAmpList.join(', ') : '');
  return payload;
};

export function sendTabNavigationAnalytics(
  oldTab: string,
  newTab: string,
  trigger: string,
  analytics: AnalyticsService,
): void {
  if (oldTab && trigger) {
    const eventName = 'navigation';
    const payloadText = (trigger === 'TAB' ? 'Button Bar: ' : 'Button Individual: ') + oldTab + ' to ' + newTab;
    analytics.setWebEvent(ENV.toolname, eventName, payloadText);
  }
}

export function loadTimeAnalytics(startDate: number, endDate: number, analytics: AnalyticsService): void {
  const eventName = 'appLoad';
  const payloadText = getElapsedInformation(endDate, startDate);
  analytics.setWebEvent(ENV.toolname, eventName, payloadText);
}

export function toolbarLinkClickAnalytics(menuLink: string, activeTab: string, analytics: AnalyticsService): void {
  const eventName = 'Toolbar Link';
  const payloadText = `${menuLink}; ${activeTab}`;
  analytics.setWebEvent(ENV.toolname, eventName, payloadText);
}

export function downloadDesignAnalytics(event: string, model: ModelService, analytics: AnalyticsService): void {
  const eventName = event === 'all' ? 'Get all files' : 'Get spice only';
  const payloadText = buildAnalyticsData(model);
  analytics.setWebEvent(ENV.toolname, eventName, payloadText);
}

export function saveDesignAnalytics(model: ModelService, analytics: AnalyticsService): void {
  const eventName = 'save';
  const payloadText = buildAnalyticsData(model);
  analytics.setWebEvent(ENV.toolname, eventName, payloadText);
}

export function loadDesignAnalytics(designData: Record<string, unknown>, analytics: AnalyticsService): void {
  const eventName = 'load';
  const now = new Date();
  const loadDate =
    now.getFullYear() +
    '-' +
    ('0' + (now.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + now.getDate()).slice(-2) +
    ' ' +
    ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
    ':' +
    ('0' + now.getMinutes()).slice(-2) +
    ':' +
    ('0' + now.getSeconds()).slice(-2) +
    ' ' +
    (now.getHours() >= 12 ? 'PM' : 'AM');
  const payload = {
    designName: designData['Name'],
    designDescription: designData['Description'],
    toolName: ENV.toolname,
    loadDate: loadDate,
  };
  analytics.setWebEvent(ENV.toolname, eventName, JSON.stringify(payload));
}

export function loadDesignUnsupportedPartAnalytics(message: string, analytics: AnalyticsService): void {
  const eventName = 'Load Design Error';
  const payload = message?.split(',').join(', ') + ' in design no longer supported';
  analytics.setWebEvent(ENV.toolname, eventName, payload);
}

export function shareDesignAnalytics(
  description: string,
  sender: string,
  recipients: string,
  designData: Record<string, unknown>,
  analytics: AnalyticsService,
): void {
  const eventName = 'toolShare';
  const payload = {
    designDescription: description,
    senderName: sender,
    emailRecipients: recipients,
    projectData: designData,
  };
  analytics.setWebEvent(ENV.toolname, eventName, JSON.stringify(payload));
}

export function sendOpenCloseDialogAnalytics(
  control: string,
  status: string,
  model: ModelService,
  analytics: AnalyticsService,
): void {
  const eventName = control + (status === 'O' ? ' first click' : status === 'S' ? ' second click' : ' cancel');
  const payloadText = buildAnalyticsData(model);
  analytics.setWebEvent(ENV.toolname, eventName, payloadText);
}

export function loadCDPAnalytics(step: string, options: cdpAnalyticsOptions, analytics: AnalyticsService): void {
  const eventName = 'CDP Load ' + step;
  const payload: cdpPayload = {};
  if (options.sId) {
    payload['sId'] = options.sId.substr(0, 8);
  }
  if (options.reqId) {
    payload['reqId'] = options.reqId.substr(0, 8);
  }
  if (options.name) {
    payload['name'] = options.name;
  }
  if (options.url) {
    payload['url'] = options.url;
  }
  if (options.status) {
    payload['status'] = options.status.code;
    payload['statusText'] = options.status.message;
  }
  if (options.timeStart) {
    if (options.timeEnd) {
      payload['time'] = +options.timeEnd - +options.timeStart;
      payload['stamp'] = options.timeEnd.toISOString();
    } else {
      payload['stamp'] = options.timeStart.toISOString();
    }
  }
  analytics.setWebEvent(ENV.toolname, eventName, JSON.stringify(payload));
}
